import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  Grid,
  Box,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Swal from 'sweetalert2';
import { gridSpacing } from 'config.js';

const apiUrl = 'https://api.academy.eurekagroup.id/courses';
const addCourseUrl = `${apiUrl}/add`;
const editCourseUrl = `${apiUrl}/edit`;
const deleteCourseUrl = (id) => `${apiUrl}/delete/${id}`;

const CoursePage = () => {
  const [courses, setCourses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [score, setScore] = useState(0);
  const [minimumPoints, setMinimumPoints] = useState(0);
  const [successPopup, setSuccessPopup] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [titleCourse, setTitleCourse] = useState('');
  const [descCourse, setDescCourse] = useState('');
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState('');
  const [reward, setReward] = useState(0);
  const [rewardType, setRewardType] = useState('points');
  const [categoryData, setCategoryData] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [thumbnail, setThumbnail] = useState(null);  // Add state for thumbnail
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ titleCourse: false });

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const response = await axios.get(apiUrl);
        setCourses(response.data); // Update the course data state
      } catch (error) {
        console.error('Error fetching courses:', error);  // Log error to console
        setErrorPopup(true);
        setMessage('Failed to fetch courses.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleSearchChange = (event) => setSearchText(event.target.value);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const token = localStorage.getItem('token'); 
    if (token) {
      try {
        const parsedToken = JSON.parse(atob(token.split('.')[1])); 
        setUsername(parsedToken.username);
        setUserId(parsedToken.userId);
      } catch (error) {
        console.error('Failed to parse token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('https://api.academy.eurekagroup.id/course-categories');
        setCategoryData(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleOpenDialog = (course) => {
    if (course) {
      setSelectedCourse(course);
      setTitleCourse(course.title_course);
      setDescCourse(course.desc_course);
      setReward(course.reward);
      setRewardType(course.reward_type);
      setScore(course.score);
      setMinimumPoints(course.minimum_points);
      setCategoryId(course.id_course_category._id);
    } else {
      setSelectedCourse(null);
      setTitleCourse('');
      setDescCourse('');
      setReward(0);
      setRewardType('points');
      setScore(0);
      setMinimumPoints(0);
      setCategoryId('');
    }
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => setOpenDialog(false);
  
  const handleFileChange = (e) => setThumbnail(e.target.files[0]);

  const handleSaveCourse = async () => {
    const courseData = new FormData();
    courseData.append("title_course", titleCourse);
    courseData.append("desc_course", descCourse);
    courseData.append("reward", reward);
    courseData.append("reward_type", rewardType);
    courseData.append("score", score);
    courseData.append("minimum_points", minimumPoints);
    courseData.append("id_employee", userId);
    courseData.append("id_course_category", categoryId);
  
    if (thumbnail) {
      courseData.append("thumbnail", thumbnail);
    }
  
    let formIsValid = true;
    const newErrors = { ...errors };
  
    // Validasi untuk setiap field
    if (!titleCourse) (newErrors.titleCourse = true), (formIsValid = false);
    else newErrors.titleCourse = false;
  
    if (!descCourse) (newErrors.descCourse = true), (formIsValid = false);
    else newErrors.descCourse = false;
  
    if (!reward) (newErrors.reward = true), (formIsValid = false);
    else newErrors.reward = false;
  
    if (!rewardType) (newErrors.rewardType = true), (formIsValid = false);
    else newErrors.rewardType = false;
  
    if (!score) (newErrors.score = true), (formIsValid = false);
    else newErrors.score = false;
  
    if (!categoryId) (newErrors.category = true), (formIsValid = false);
    else newErrors.category = false;
  
    if (!thumbnail) (newErrors.thumbnail = true), (formIsValid = false);
    else newErrors.thumbnail = false;
  
    setErrors(newErrors);
  
    if (!formIsValid) return;
  
    const token = localStorage.getItem("token");
    setLoading(true);
  
    try {
      let response;
      if (selectedCourse) {
        // Update existing course
        courseData.append("_id", selectedCourse._id);
        response = await axios.put(editCourseUrl, courseData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        // Add new course
        response = await axios.post(addCourseUrl, courseData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setCourses((prevCourses) => [...prevCourses, response.data]); // Update state tanpa reload
      }
  
      setSuccessPopup(true); // Tampilkan popup sukses
      handleCloseDialog(); // Tutup dialog
  
      // Gunakan SweetAlert dan reload setelah user klik "OK"
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: selectedCourse
          ? "Course updated successfully."
          : "Course added successfully.",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.reload();
      });
  
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while saving the course.",
      });
    } finally {
      setLoading(false);
    }
  };  

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      const token = localStorage.getItem('token'); // Ambil token dari localStorage
      setLoading(true);
  
      try {
        await axios.delete(deleteCourseUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Bearer Token
          },
        });
        setCourses(courses.filter((course) => course._id !== id));
        setMessage('Course deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        setErrorPopup(true);
        setMessage('Failed to delete course.');
      } finally {
        setLoading(false);
      }
    }
  };  

  const filteredCourses = courses.filter((course) =>
    course.title_course.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" justifyContent="space-between">
                  <h1>Course Management</h1>
                  <Box>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      value={searchText}
                      onChange={handleSearchChange}
                      sx={{ marginRight: 2 }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog(null)}>
                      Add Course
                    </Button>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>No</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Reward</TableCell>
                      <TableCell>Author</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((course, index) => (
                      <TableRow key={course._id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                        {course.thumbnail && (
                          <img
                            src={`https://api.academy.eurekagroup.id/uploads/course/${course.thumbnail}`}
                            alt="Thumbnail"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover', // Memastikan gambar tidak terdistorsi dan tetap dalam ukuran kotak
                              borderRadius: '4px', // Opsional: menambahkan border radius agar gambar lebih melengkung
                            }}
                          />
                        )}
                      </TableCell>
                        <TableCell>{course.title_course}</TableCell>
                        <TableCell>{course.id_course_category.title_category}</TableCell>
                        <TableCell>{course.desc_course}</TableCell>
                        <TableCell>{course.reward}</TableCell>
                        <TableCell>{username}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleOpenDialog(course)}><EditIcon /></IconButton>
                          <IconButton onClick={() => handleDeleteClick(course._id)}><DeleteIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredCourses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog for Add/Edit Course */}
      <Dialog
  open={openDialog}
  onClose={(e, reason) => {
    if (reason === "backdropClick") return;
  }}
>
  <DialogTitle>{selectedCourse ? "Edit Course" : "Add Course"}</DialogTitle>
  <DialogContent>
    <Grid container spacing={2}>
      {/* Kolom Kiri */}
      <Grid item xs={12} sm={6}>
        {/* Title Field */}
        <TextField
          label="Title"
          variant="outlined"
          fullWidth
          value={titleCourse}
          onChange={(e) => {
            if (e.target.value.length <= 20) {
              setTitleCourse(e.target.value);
            }
          }}
          error={errors.titleCourse}
          helperText={
            errors.titleCourse
              ? "Title is required"
              : titleCourse.length > 20
              ? "Maximum 20 characters allowed"
              : ""
          }
          inputProps={{ maxLength: 20 }}
          sx={{ marginBottom: 2 }}
        />

        {/* Category Select */}
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <Select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="">
              <em>Select Category</em>
            </MenuItem>
            {categoryData.map((category) => (
              <MenuItem key={category._id} value={category._id}>
                {category.title_category}
              </MenuItem>
            ))}
          </Select>
          {errors.category && (
            <div style={{ color: "red" }}>Category is required</div>
          )}
        </FormControl>

        {/* Author (Read-Only) */}
        <TextField
          label="Author"
          sx={{ marginBottom: 2 }}
          fullWidth
          value={username}
          InputProps={{
            readOnly: true,
          }}
        />

        {/* Reward Field */}
        <TextField
          label="Reward"
          variant="outlined"
          fullWidth
          value={reward}
          onChange={(e) => setReward(e.target.value)}
          error={errors.reward}
          helperText={errors.reward ? "Reward is required" : ""}
          sx={{ marginBottom: 2 }}
          type="number"
        />
      </Grid>

      {/* Kolom Kanan */}
      <Grid item xs={12} sm={6}>
        {/* Reward Type Select */}
        <FormControl fullWidth sx={{ marginBottom: 2 }} error={errors.rewardType}>
          <Select
            value={rewardType}
            onChange={(e) => setRewardType(e.target.value)}
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="">
              <em>Select Reward Type</em>
            </MenuItem>
            <MenuItem value="points">Points</MenuItem>
          </Select>
          {errors.rewardType && (
            <div style={{ color: "red", marginTop: 4 }}>
              Reward type is required
            </div>
          )}
        </FormControl>

        {/* Score Field */}
        <TextField
          label="Minimum Score"
          variant="outlined"
          fullWidth
          value={score}
          onChange={(e) => setScore(e.target.value)}
          error={errors.score}
          helperText={errors.score ? "Score is required" : ""}
          sx={{ marginBottom: 2 }}
          type="number"
        />

        {/* Minimum Points Field */}
        <TextField
          label="Minimum Points"
          variant="outlined"
          fullWidth
          value={minimumPoints}
          onChange={(e) => setMinimumPoints(e.target.value)}
          error={errors.minimumPoints}
          helperText={errors.minimumPoints}
          sx={{ marginBottom: 2 }}
          type="number"
        />

        {/* File Upload */}
        <input
          type="file"
          onChange={handleFileChange}
          style={{ marginBottom: 10 }}
        />
        {errors.thumbnail && (
          <div style={{ color: "red", fontSize: "0.875rem" }}>
            Thumbnail is required
          </div>
        )}
      </Grid>

      {/* Description (1 Kolom Penuh) */}
      <Grid item xs={12}>
        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          value={descCourse}
          onChange={(e) => setDescCourse(e.target.value)}
          sx={{ marginBottom: 2 }}
          error={errors.descCourse}
          helperText={errors.descCourse ? "Description is required" : ""}
          multiline
          rows={3}
        />
      </Grid>
    </Grid>
  </DialogContent>

  {/* Actions for Save/Cancel */}
  <DialogActions>
    <Button onClick={handleCloseDialog} color="secondary">
      Cancel
    </Button>
    <Button onClick={handleSaveCourse} color="primary">
      Save
    </Button>
  </DialogActions>
</Dialog>

      {/* Backdrop Loader */}
      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Success Snackbar */}
      <Snackbar
        open={successPopup}
        message={message}
        autoHideDuration={3000}
        onClose={() => setSuccessPopup(false)}
      />
      
      {/* Error Snackbar */}
      <Snackbar
        open={errorPopup}
        message={message}
        autoHideDuration={3000}
        onClose={() => setErrorPopup(false)}
      />
    </>
  );
};

export default CoursePage;
